/**
 * Reine Regeln der Karte „Heat-Up-Wellen je Liste" (Workshop-Lücke N2, Plan 2026-10-29): Grau-Gründe und Heatmap-Farbe.
 * Getestet in `matrix.test.ts`. Die Zahlen selbst kommen fertig aus dem Backend (`hebelService.wellenMatrixAus`).
 */
import type { HebelGrauSchwellen, MatrixZeile, MatrixZelle, WelleKey, WellenQuoten, WellenZahlen } from '../../../helpers/hebelData';

type Zahlen = Pick<WellenZahlen, 'anmeldungen' | 'anfos' | 'anfos_vorlaeufig' | 'abmeldungen'>;

/** Anmeldungen je 1.000 (Hauptwert) unsicher? Unter der Seiten-Schwelle der Anmeldungen. */
export function anmeldungenGrau(z: Zahlen, g: HebelGrauSchwellen): string | null {
  return z.anmeldungen < g.anmeldungen ? `unter ${g.anmeldungen} Anmeldungen — der Wert kann am Zufall hängen` : null;
}

/** € je 1.000 erst ab `g.anfos` Anfos (ohne vorläufige); darunter steht nur die Zahl der Anfos. */
export function euroZeigen(z: Zahlen, g: HebelGrauSchwellen): boolean {
  return z.anfos - z.anfos_vorlaeufig >= g.anfos;
}

export function abmeldungenGrau(z: Zahlen, g: HebelGrauSchwellen): string | null {
  return z.abmeldungen < g.abmeldungen ? `unter ${g.abmeldungen} Abmeldungen` : null;
}

/** `#2a78d6` + 0.3 → `rgba(42, 120, 214, 0.3)`. */
export function mitAlpha(hex: string, alpha: number): string {
  const h = hex.replace('#', '');
  const v = h.length === 3 ? h.split('').map((c) => c + c).join('') : h;
  const n = parseInt(v, 16);
  return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${Math.round(alpha * 1000) / 1000})`;
}

/**
 * Hintergrund einer Zelle nach ihrem Index (Zeilenschnitt = 100): über 100 in `oben`, darunter in `unten`, Stärke nach dem
 * Abstand auf der Log-Skala (Index 150 bzw. 67 = volle Stärke). Ohne Index oder bei unsicherem Wert keine Farbe — die
 * Zahl steht immer daneben, die Farbe trägt nichts allein.
 */
export function indexFarbe(index: number | null, grau: boolean, oben: string, unten: string): string | undefined {
  if (index === null || grau || index <= 0) return undefined;
  const t = Math.min(1, Math.abs(Math.log(index / 100)) / Math.log(1.5));
  if (t < 0.05) return undefined;
  return mitAlpha(index >= 100 ? oben : unten, 0.08 + 0.32 * t);
}

/** Sortierwert einer Wellen-Spalte: Anmeldungen je 1.000, leere Zelle `null` (steht in beiden Richtungen am Ende). */
export const zellSort = (z: (MatrixZelle & WellenQuoten) | null): number | null => (z ? z.anmeldungen_je_1000 : null);

const WELLEN_NAME: Record<WelleKey, string> = { vortag: 'Vortag', frueh: 'Webinartag früh', spaet: 'Webinartag spät' };

/**
 * Befund der Matrix (Konsistenzprüfung 29.10.: einzige Hebel-Karte ohne Befundzeile). Je Liste die Welle mit dem höchsten
 * Index gegen den EIGENEN Schnitt — nur über sichere Zellen (ab der Grau-Schwelle der Anmeldungen) und nur für Listen mit
 * mindestens zwei sicheren Wellen; nie ein Vergleich zwischen Listen. Höchstens drei Listen in API-Reihenfolge.
 */
export function matrixBefund(zeilen: MatrixZeile[], g: HebelGrauSchwellen): string | null {
  const teile: string[] = [];
  for (const z of zeilen) {
    const sicher = (Object.keys(WELLEN_NAME) as WelleKey[])
      .map((k) => ({ k, c: z.zellen[k] }))
      .filter((x): x is { k: WelleKey; c: MatrixZelle } => !!x.c && x.c.index !== null && anmeldungenGrau(x.c, g) === null);
    if (sicher.length < 2) continue;
    const best = sicher.reduce((a, b) => ((b.c.index as number) > (a.c.index as number) ? b : a));
    teile.push(`${z.label} ${WELLEN_NAME[best.k]} (Index ${Math.round(best.c.index as number)})`);
    if (teile.length === 3) break;
  }
  return teile.length ? `Stärkste Welle je Liste, gemessen am eigenen Schnitt: ${teile.join(', ')}.` : null;
}
